import { Injectable } from '@angular/core';
import { AppUpdate, AppUpdateAvailability, AppUpdateInfo } from '@capawesome/capacitor-app-update';
import { BehaviorSubject, from, of, switchMap } from 'rxjs';
import { catchError, first, map } from 'rxjs/operators';
import { AppUpdateResultCode } from '@capawesome/capacitor-app-update/dist/esm/definitions';
import { ModalService } from './modal.service';
import { ToastService } from './toast.service';
import { ModalUpdateAppComponent } from '../../components/modal-update-app/modal-update-app.component';

@Injectable({ providedIn: 'root' })
export class UpdateAppService {
    public appUpdateInfo$ = new BehaviorSubject<AppUpdateInfo | null>(null);
    public isOpenUpdateModal$ = new BehaviorSubject<boolean | null>(null);
    public updateAvailability$ = new BehaviorSubject<boolean | null>(null);
    constructor(private modalService: ModalService, private toastService: ToastService) {}

    public checkUpdate(isOpenUpdate: boolean = true) {
        return from(AppUpdate.getAppUpdateInfo()).pipe(
            catchError(() => {
                this.isOpenUpdateModal$.next(false);

                return of(null);
            }),
            switchMap(appUpdateInfo => {
                console.log('appUpdateInfo', appUpdateInfo);
                this.appUpdateInfo$.next(appUpdateInfo);

                if (appUpdateInfo?.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE) {
                    this.updateAvailability$.next(false);
                    this.isOpenUpdateModal$.next(false);

                    return of(false);
                }

                this.updateAvailability$.next(true);

                if (isOpenUpdate) {
                    this.openUpdateMethod(appUpdateInfo?.immediateUpdateAllowed);

                    return of(true);
                }

                this.isOpenUpdateModal$.next(false);

                return of(true);
            }),
        );
    }

    public openUpdateMethod(immediateUpdateAllowed?: boolean | undefined) {
        this.isOpenUpdateModal$.next(true);
        if (immediateUpdateAllowed || this.appUpdateInfo$.value?.immediateUpdateAllowed) {
            this.openImmediateUpdate();
        } else {
            this.openUpdateModal();
        }
    }

    public openImmediateUpdate() {
        from(AppUpdate.performImmediateUpdate())
            .pipe(
                first(),
                switchMap(result => {
                    switch (result.code) {
                        case AppUpdateResultCode.FAILED:
                        case AppUpdateResultCode.INFO_MISSING:
                        case AppUpdateResultCode.NOT_ALLOWED:
                        case AppUpdateResultCode.NOT_AVAILABLE:
                            return this.toastService
                                .openToast('update-modal.immediateResults.failed', 'danger', 'center bottom', 2000)
                                .pipe(map(() => result));

                        case AppUpdateResultCode.CANCELED:
                            return this.toastService
                                .openToast('update-modal.immediateResults.canceled', 'danger', 'center bottom', 2000)
                                .pipe(map(() => result));

                        default:
                            return of(result);
                    }
                }),
                catchError(() => {
                    return of(null);
                }),
            )
            .subscribe(result => {
                this.isOpenUpdateModal$.next(false);
                console.log('performImmediateUpdate result', result);
            });
    }

    public openUpdateModal() {
        this.modalService
            .openModalComponent(ModalUpdateAppComponent)
            .pipe(first())
            .subscribe(result => {
                this.isOpenUpdateModal$.next(false);
                console.log('ModalUpdateAppComponent result', result);
            });
    }

    public async openAppStore(): Promise<void> {
        await AppUpdate.openAppStore();
    }

    public async performImmediateUpdate(): Promise<void> {
        await AppUpdate.performImmediateUpdate();
    }

    public async startFlexibleUpdate(): Promise<void> {
        await AppUpdate.startFlexibleUpdate();
    }

    public async completeFlexibleUpdate(): Promise<void> {
        await AppUpdate.completeFlexibleUpdate();
    }
}
